/**
 * CommunityModule -- Full social features: Groups, Challenges, Tournaments, Leaderboard.
 *
 * Four-tab layout driving retention through social competition and collaboration.
 * Uses Identity API for groups/challenges, Games API for tournaments, and
 * aggregated scoring for leaderboards.
 */

import type { AweniContext, AweniModule } from '../../types';
import { ChallengeCard, type ChallengeData } from './ChallengeCard';
import { TournamentBracket, type TournamentData, type BracketMatch } from './TournamentBracket';
import { GroupChat } from './GroupChat';

// ---------- Types ----------

interface CommunityGroup {
  id: string;
  name: string;
  description?: string;
  type: 'family' | 'friends' | 'school' | 'club';
  memberCount: number;
  lastActivity: string;
  avatar?: string;
  role?: 'owner' | 'admin' | 'member';
}

interface GroupMember {
  id: string;
  name: string;
  avatar?: string;
  role: 'owner' | 'admin' | 'member';
  lastSeen: string;
}

interface GroupStats {
  gamesPlayed: number;
  totalExpenses: number;
  activeStreaks: number;
  challengesWon: number;
}

interface LeaderboardEntry {
  userId: string;
  name: string;
  avatar?: string;
  points: number;
  rank: number;
  trend: 'up' | 'down' | 'same';
  isCurrentUser?: boolean;
}

type TabId = 'groups' | 'challenges' | 'tournaments' | 'leaderboard';
type TimeRange = 'week' | 'month' | 'all';
type LeaderboardCategory = 'overall' | 'games' | 'memorization' | 'reading';

// ---------- Mock Data ----------

function getMockGroups(): CommunityGroup[] {
  return [
    { id: '1', name: 'Family Learning', description: 'Our family learning journey', type: 'family', memberCount: 5, lastActivity: new Date(Date.now() - 3600000).toISOString(), role: 'owner' },
    { id: '2', name: 'Math Champions', description: 'Competitive math practice group', type: 'school', memberCount: 12, lastActivity: new Date(Date.now() - 86400000).toISOString(), role: 'member' },
    { id: '3', name: 'Quran Study Circle', description: 'Weekly Quran memorization support', type: 'club', memberCount: 8, lastActivity: new Date(Date.now() - 172800000).toISOString(), role: 'admin' },
  ];
}

function getMockMembers(): GroupMember[] {
  return [
    { id: 'u1', name: 'Ahmed K.', role: 'owner', lastSeen: new Date(Date.now() - 300000).toISOString() },
    { id: 'u2', name: 'Sara M.', role: 'admin', lastSeen: new Date(Date.now() - 1800000).toISOString() },
    { id: 'u3', name: 'Youssef B.', role: 'member', lastSeen: new Date(Date.now() - 7200000).toISOString() },
    { id: 'u4', name: 'Fatima A.', role: 'member', lastSeen: new Date(Date.now() - 86400000).toISOString() },
    { id: 'u5', name: 'Omar T.', role: 'member', lastSeen: new Date(Date.now() - 172800000).toISOString() },
  ];
}

function getMockChallenges(): ChallengeData[] {
  return [
    { id: 'c1', title: '7-Day Streak Challenge', description: 'Maintain daily activity for 7 consecutive days', type: 'streak', target: 7, progress: 4, participantCount: 23, startDate: new Date(Date.now() - 4 * 86400000).toISOString(), endDate: new Date(Date.now() + 3 * 86400000).toISOString(), joined: true, completed: false },
    { id: 'c2', title: 'Math Master', description: 'Play 20 math games this week', type: 'games', target: 20, progress: 8, participantCount: 45, startDate: new Date(Date.now() - 2 * 86400000).toISOString(), endDate: new Date(Date.now() + 5 * 86400000).toISOString(), joined: true, completed: false },
    { id: 'c3', title: 'Reading Marathon', description: 'Read 10 articles this month', type: 'reading', target: 10, progress: 3, participantCount: 67, startDate: new Date(Date.now() - 10 * 86400000).toISOString(), endDate: new Date(Date.now() + 20 * 86400000).toISOString(), joined: false, completed: false },
    { id: 'c4', title: 'Memorization Sprint', description: 'Review 100 cards in 5 days', type: 'memorization', target: 100, progress: 100, participantCount: 34, startDate: new Date(Date.now() - 7 * 86400000).toISOString(), endDate: new Date(Date.now() - 2 * 86400000).toISOString(), joined: true, completed: true },
  ];
}

function getMockTournaments(): TournamentData[] {
  return [
    { id: 't1', title: 'Math Showdown', gameKey: 'math-challenge', gameThumbnail: '', format: 'highscore', participantCount: 16, maxParticipants: 16, status: 'active', startDate: new Date(Date.now() - 2 * 86400000).toISOString(), endDate: new Date(Date.now() + 5 * 86400000).toISOString(), description: 'Compete for the highest math score!', joined: true },
    { id: 't2', title: 'Logic League', gameKey: 'logic-puzzle', gameThumbnail: '', format: 'bracket', participantCount: 8, maxParticipants: 16, status: 'registration', startDate: new Date(Date.now() + 3 * 86400000).toISOString(), endDate: new Date(Date.now() + 10 * 86400000).toISOString(), description: 'Single-elimination logic puzzle tournament', joined: false },
    { id: 't3', title: 'Word Builder Cup', gameKey: 'word-builder', gameThumbnail: '', format: 'highscore', participantCount: 32, maxParticipants: 32, status: 'completed', startDate: new Date(Date.now() - 14 * 86400000).toISOString(), endDate: new Date(Date.now() - 7 * 86400000).toISOString(), description: 'Who has the largest vocabulary?', joined: true, winnerName: 'Sara M.' },
  ];
}

function getMockLeaderboard(currentUserId: string): LeaderboardEntry[] {
  const names = ['Sara M.', 'Ahmed K.', 'Youssef B.', 'Fatima A.', 'Omar T.', 'Nadia H.', 'Hassan R.', 'Leila S.', 'Karim D.', 'Amina L.', 'Bilal F.', 'Hana Z.', 'Tariq N.', 'Salma W.', 'Ziad C.', 'Meryem P.', 'Ali G.', 'Dina Q.', 'Rami O.', 'Ines B.'];
  const points = [1250, 1180, 1045, 980, 920, 870, 830, 790, 745, 710, 680, 650, 620, 590, 560, 530, 500, 470, 440, 410];
  const trends: ('up' | 'down' | 'same')[] = ['up', 'same', 'up', 'down', 'up', 'same', 'down', 'up', 'same', 'up', 'down', 'up', 'same', 'down', 'up', 'same', 'up', 'down', 'same', 'up'];
  return names.map((name, i) => ({
    userId: `u${i + 1}`,
    name,
    points: points[i],
    rank: i + 1,
    trend: trends[i],
    isCurrentUser: `u${i + 1}` === currentUserId || i === 6,
  }));
}

function getMockBracketMatches(): BracketMatch[] {
  return [
    // Round 1 (4 matches)
    { id: 'm1', round: 1, position: 0, player1: { name: 'Ahmed K.', score: 850 }, player2: { name: 'Sara M.', score: 920 }, winnerId: 'p2', completed: true },
    { id: 'm2', round: 1, position: 1, player1: { name: 'Omar T.', score: 780 }, player2: { name: 'Fatima A.', score: 810 }, winnerId: 'p2', completed: true },
    { id: 'm3', round: 1, position: 2, player1: { name: 'Youssef B.', score: 900 }, player2: { name: 'Nadia H.', score: 870 }, winnerId: 'p1', completed: true },
    { id: 'm4', round: 1, position: 3, player1: { name: 'Hassan R.', score: 0 }, player2: { name: 'Leila S.', score: 0 }, completed: false },
    // Semi-finals (2 matches)
    { id: 'm5', round: 2, position: 0, player1: { name: 'Sara M.', score: 950 }, player2: { name: 'Fatima A.', score: 880 }, winnerId: 'p1', completed: true },
    { id: 'm6', round: 2, position: 1, player1: { name: 'Youssef B.', score: 0 }, player2: { name: 'TBD', score: 0 }, completed: false },
    // Final
    { id: 'm7', round: 3, position: 0, player1: { name: 'Sara M.', score: 0 }, player2: { name: 'TBD', score: 0 }, completed: false },
  ];
}

// ---------- Helpers ----------

function formatTimeAgo(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return diffMin + 'm ago';
  if (diffHr < 24) return diffHr + 'h ago';
  return diffDay + 'd ago';
}

function daysRemaining(endDate: string): number {
  return Math.max(0, Math.ceil((new Date(endDate).getTime() - Date.now()) / 86400000));
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for future activity-icon rendering
function getTypeIcon(type: string): string {
  const icons: Record<string, string> = {
    streak: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>',
    games: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"/></svg>',
    reading: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>',
    memorization: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="18" rx="2"/><path d="M8 7h8M8 11h6M8 15h4"/></svg>',
  };
  return icons[type] || icons.games;
}

function getTypeColor(type: string): string {
  const colors: Record<string, string> = { streak: '#F59E0B', games: '#3B82F6', reading: '#14B8A6', memorization: '#8B5CF6' };
  return colors[type] || '#6366F1';
}

function getRoleBadgeColor(role: string): string {
  if (role === 'owner') return '#EF4444';
  if (role === 'admin') return '#F59E0B';
  return '#6B7280';
}

function getGroupTypeColor(type: string): string {
  const colors: Record<string, string> = { family: '#0A0A0A', friends: '#3B82F6', school: '#F59E0B', club: '#8B5CF6' };
  return colors[type] || '#6366F1';
}

function getTrendIcon(trend: 'up' | 'down' | 'same'): string {
  if (trend === 'up') return '<svg class="w-4 h-4" style="color:#0A0A0A" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="18 15 12 9 6 15"/></svg>';
  if (trend === 'down') return '<svg class="w-4 h-4" style="color:#EF4444" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>';
  return '<svg class="w-4 h-4" style="color:#6B7280" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>';
}

function getInitials(name: string): string {
  return name.split(' ').map((w) => w.charAt(0)).join('').toUpperCase().slice(0, 2);
}

function getStatusColor(status: string): string {
  if (status === 'active') return '#0A0A0A';
  if (status === 'registration') return '#3B82F6';
  return '#6B7280';
}

// ---------- SVG Icons ----------
const ICONS = {
  users: '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
  trophy: '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>',
  star: '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
  chart: '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="14" width="4" height="8"/><rect x="10" y="8" width="4" height="14"/><rect x="16" y="4" width="4" height="18"/></svg>',
  plus: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>',
  back: '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>',
  send: '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>',
  link: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
  message: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  chevron: '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>',
  play: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>',
  crown: '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z"/><path d="M3 20h18"/></svg>',
};

// ---------- Module ----------

export default class CommunityModule implements AweniModule {
  private container: HTMLElement | null = null;
  private ctx: AweniContext | null = null;
  private activeTab: TabId = 'groups';
  private activeGroupDetail: CommunityGroup | null = null;
  private activeTournamentDetail: TournamentData | null = null;
  private activeChallengeDetail: ChallengeData | null = null;
  private groupChatInstance: GroupChat | null = null;
  private leaderboardRange: TimeRange = 'week';
  private leaderboardCategory: LeaderboardCategory = 'overall';
  private showCreateGroupModal = false;
  private showCreateChallengeModal = false;
  private showJoinGroupModal = false;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.ctx = ctx;
    this.activeTab = 'groups';
    this.activeGroupDetail = null;
    this.activeTournamentDetail = null;
    this.activeChallengeDetail = null;
    await this.render();
  }

  unmount(): void {
    if (this.groupChatInstance) {
      this.groupChatInstance.destroy();
      this.groupChatInstance = null;
    }
    if (this.container) {
      this.container.innerHTML = '';
      this.container = null;
    }
    this.ctx = null;
  }

  // ---------- Main Render ----------

  private async render(): Promise<void> {
    if (!this.container || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';

    // If showing group detail, render that instead
    if (this.activeGroupDetail) {
      await this.renderGroupDetail();
      return;
    }
    if (this.activeTournamentDetail) {
      await this.renderTournamentDetail();
      return;
    }
    if (this.activeChallengeDetail) {
      await this.renderChallengeDetail();
      return;
    }

    const tabs: { id: TabId; key: string; icon: string; color: string }[] = [
      { id: 'groups', key: 'community_tab_groups', icon: ICONS.users, color: '#6366F1' },
      { id: 'challenges', key: 'community_tab_challenges', icon: ICONS.star, color: '#EF4444' },
      { id: 'tournaments', key: 'community_tab_tournaments', icon: ICONS.trophy, color: '#F59E0B' },
      { id: 'leaderboard', key: 'community_tab_leaderboard', icon: ICONS.chart, color: '#3B82F6' },
    ];

    this.container.innerHTML = `
      <div class="community-module pb-24 pt-2 max-w-3xl mx-auto" dir="${isRtl ? 'rtl' : 'ltr'}">
        <!-- Header -->
        <div class="px-4 mb-4">
          <h1 class="text-xl font-bold" style="color: var(--text-primary)">${t('community_title')}</h1>
        </div>

        <!-- Tab Bar -->
        <div class="px-4 mb-5">
          <div class="flex gap-1 p-1 rounded-2xl" style="background: var(--bg-secondary)">
            ${tabs.map((tab) => `
              <button class="community-tab flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl text-xs font-semibold transition-all ${this.activeTab === tab.id ? 'shadow-sm' : ''}" data-tab="${tab.id}"
                style="${this.activeTab === tab.id ? `background: var(--card-bg); color: ${tab.color}` : 'background: transparent; color: var(--text-secondary)'}"
                aria-selected="${this.activeTab === tab.id}">
                <span class="w-4 h-4 flex items-center justify-center">${tab.icon}</span>
                <span class="hidden sm:inline">${t(tab.key)}</span>
              </button>
            `).join('')}
          </div>
        </div>

        <!-- Tab Content -->
        <div class="px-4" id="community-tab-content">
          ${t('common_loading')}
        </div>
      </div>

      <!-- Create Group Modal -->
      ${this.showCreateGroupModal ? this.renderCreateGroupModal(t) : ''}
      <!-- Join Group Modal -->
      ${this.showJoinGroupModal ? this.renderJoinGroupModal(t) : ''}
      <!-- Create Challenge Modal -->
      ${this.showCreateChallengeModal ? this.renderCreateChallengeModal(t) : ''}
    `;

    // Bind tab clicks
    this.container.querySelectorAll<HTMLButtonElement>('.community-tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.activeTab = btn.dataset.tab as TabId;
        this.render();
      });
    });

    // Render active tab content
    const content = this.container.querySelector('#community-tab-content');
    if (content) {
      switch (this.activeTab) {
        case 'groups': await this.renderGroupsTab(content as HTMLElement); break;
        case 'challenges': await this.renderChallengesTab(content as HTMLElement); break;
        case 'tournaments': await this.renderTournamentsTab(content as HTMLElement); break;
        case 'leaderboard': await this.renderLeaderboardTab(content as HTMLElement); break;
      }
    }

    this.bindModalEvents();
  }

  // ---------- Tab 1: Groups ----------

  private async renderGroupsTab(container: HTMLElement): Promise<void> {
    if (!this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';

    let groups: CommunityGroup[];
    try {
      groups = await this.ctx.api.get<CommunityGroup[]>('/aweni/api/v1/identity/groups');
    } catch {
      groups = getMockGroups();
    }

    container.innerHTML = `
      <!-- Actions row -->
      <div class="flex gap-2 mb-4">
        <button id="btn-join-group" class="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-transform active:scale-95" style="background: var(--bg-secondary); color: var(--text-primary)">
          ${ICONS.link} ${t('community_join_group')}
        </button>
        <button id="btn-create-group" class="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-transform active:scale-95" style="background: #6366F1; color: white">
          ${ICONS.plus} ${t('community_create_group')}
        </button>
      </div>

      ${groups.length > 0 ? `
        <div class="space-y-3">
          ${groups.map((g) => `
            <div class="community-group-card flex items-center gap-3 p-4 rounded-2xl transition-transform active:scale-[0.98] cursor-pointer" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-group-id="${g.id}" role="button" tabindex="0">
              <div class="w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style="background: ${getGroupTypeColor(g.type)}20; color: ${getGroupTypeColor(g.type)}">
                ${getInitials(g.name)}
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <p class="text-sm font-semibold truncate" style="color: var(--text-primary)">${g.name}</p>
                  ${g.role && g.role !== 'member' ? `<span class="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" style="background: ${getRoleBadgeColor(g.role)}20; color: ${getRoleBadgeColor(g.role)}">${t('community_role_' + g.role)}</span>` : ''}
                </div>
                <div class="flex items-center gap-2 mt-0.5">
                  <span class="text-xs" style="color: var(--text-secondary)">${t('community_members', { count: g.memberCount })}</span>
                  <span class="text-xs" style="color: var(--text-secondary)">&middot;</span>
                  <span class="text-xs" style="color: var(--text-secondary)">${formatTimeAgo(g.lastActivity)}</span>
                </div>
              </div>
              <span class="${isRtl ? 'rotate-180' : ''}" style="color: var(--text-secondary)">${ICONS.chevron}</span>
            </div>
          `).join('')}
        </div>
      ` : `
        <div class="text-center py-16">
          <div class="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style="background: #6366F120; color: #6366F1">
            ${ICONS.users}
          </div>
          <p class="text-sm mb-2 font-semibold" style="color: var(--text-primary)">${t('community_no_groups')}</p>
          <p class="text-xs mb-6" style="color: var(--text-secondary)">${t('community_no_groups_sub')}</p>
        </div>
      `}
    `;

    // Bind group card clicks
    container.querySelectorAll<HTMLElement>('.community-group-card').forEach((card) => {
      card.addEventListener('click', () => {
        const gId = card.dataset.groupId;
        const group = groups.find((g) => g.id === gId);
        if (group) {
          this.activeGroupDetail = group;
          this.render();
        }
      });
    });

    container.querySelector('#btn-create-group')?.addEventListener('click', () => {
      this.showCreateGroupModal = true;
      this.render();
    });

    container.querySelector('#btn-join-group')?.addEventListener('click', () => {
      this.showJoinGroupModal = true;
      this.render();
    });
  }

  // ---------- Group Detail View ----------

  private async renderGroupDetail(): Promise<void> {
    if (!this.container || !this.ctx || !this.activeGroupDetail) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';
    const group = this.activeGroupDetail;

    let members: GroupMember[];
    try {
      members = await this.ctx.api.get<GroupMember[]>(`/aweni/api/v1/identity/groups/${group.id}/members`);
    } catch {
      members = getMockMembers();
    }

    const stats: GroupStats = { gamesPlayed: 156, totalExpenses: 42, activeStreaks: 3, challengesWon: 7 };

    this.container.innerHTML = `
      <div class="community-module pb-24 pt-2 max-w-3xl mx-auto" dir="${isRtl ? 'rtl' : 'ltr'}">
        <!-- Back Button + Header -->
        <div class="px-4 mb-4 flex items-center gap-3">
          <button id="btn-back-groups" class="w-10 h-10 rounded-full flex items-center justify-center transition-transform active:scale-95 ${isRtl ? 'rotate-180' : ''}" style="background: var(--bg-secondary); color: var(--text-primary)">
            ${ICONS.back}
          </button>
          <div class="flex-1 min-w-0">
            <h1 class="text-lg font-bold truncate" style="color: var(--text-primary)">${group.name}</h1>
            <p class="text-xs" style="color: var(--text-secondary)">${t('community_members', { count: group.memberCount })}</p>
          </div>
          <div class="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold" style="background: ${getGroupTypeColor(group.type)}20; color: ${getGroupTypeColor(group.type)}">
            ${getInitials(group.name)}
          </div>
        </div>

        ${group.description ? `<p class="px-4 mb-4 text-sm" style="color: var(--text-secondary)">${group.description}</p>` : ''}

        <!-- Quick Actions -->
        <div class="px-4 mb-5 grid grid-cols-3 gap-2">
          <button class="group-action flex flex-col items-center gap-1.5 py-3 rounded-xl transition-transform active:scale-95" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-action="message">
            <span style="color: #3B82F6">${ICONS.message}</span>
            <span class="text-[10px] font-semibold" style="color: var(--text-primary)">${t('community_send_message')}</span>
          </button>
          <button class="group-action flex flex-col items-center gap-1.5 py-3 rounded-xl transition-transform active:scale-95" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-action="challenge">
            <span style="color: #EF4444">${ICONS.star}</span>
            <span class="text-[10px] font-semibold" style="color: var(--text-primary)">${t('community_create_challenge')}</span>
          </button>
          <button class="group-action flex flex-col items-center gap-1.5 py-3 rounded-xl transition-transform active:scale-95" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-action="invite">
            <span style="color: #0A0A0A">${ICONS.link}</span>
            <span class="text-[10px] font-semibold" style="color: var(--text-primary)">${t('community_share_invite')}</span>
          </button>
        </div>

        <!-- Stats -->
        <div class="px-4 mb-5">
          <div class="grid grid-cols-4 gap-2">
            ${[
              { value: stats.gamesPlayed, label: t('community_stat_games'), color: '#3B82F6' },
              { value: stats.totalExpenses, label: t('community_stat_expenses'), color: '#8B5CF6' },
              { value: stats.activeStreaks, label: t('community_stat_streaks'), color: '#F59E0B' },
              { value: stats.challengesWon, label: t('community_stat_challenges'), color: '#0A0A0A' },
            ].map((s) => `
              <div class="rounded-xl p-3 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
                <p class="text-lg font-bold" style="color: ${s.color}">${s.value}</p>
                <p class="text-[10px]" style="color: var(--text-secondary)">${s.label}</p>
              </div>
            `).join('')}
          </div>
        </div>

        <!-- Members -->
        <div class="px-4 mb-5">
          <h2 class="text-sm font-semibold mb-3 uppercase tracking-wide" style="color: var(--text-secondary)">${t('community_members_title')}</h2>
          <div class="rounded-2xl overflow-hidden" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
            ${members.map((m) => `
              <div class="flex items-center gap-3 px-4 py-3 border-b last:border-b-0" style="border-color: var(--border)">
                <div class="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style="background: #6366F120; color: #6366F1">
                  ${getInitials(m.name)}
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium truncate" style="color: var(--text-primary)">${m.name}</p>
                  <p class="text-xs" style="color: var(--text-secondary)">${formatTimeAgo(m.lastSeen)}</p>
                </div>
                <span class="px-2 py-0.5 rounded text-[10px] font-bold uppercase" style="background: ${getRoleBadgeColor(m.role)}20; color: ${getRoleBadgeColor(m.role)}">${t('community_role_' + m.role)}</span>
              </div>
            `).join('')}
          </div>
        </div>

        <!-- Group Chat -->
        <div class="px-4 mb-5">
          <h2 class="text-sm font-semibold mb-3 uppercase tracking-wide" style="color: var(--text-secondary)">${t('community_group_chat')}</h2>
          <div id="group-chat-container" class="rounded-2xl overflow-hidden" style="background: var(--card-bg); box-shadow: var(--shadow-sm)"></div>
        </div>
      </div>
    `;

    // Back button
    this.container.querySelector('#btn-back-groups')?.addEventListener('click', () => {
      this.activeGroupDetail = null;
      if (this.groupChatInstance) {
        this.groupChatInstance.destroy();
        this.groupChatInstance = null;
      }
      this.render();
    });

    // Group actions
    this.container.querySelectorAll<HTMLElement>('.group-action').forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        if (action === 'message') {
          const chatContainer = this.container?.querySelector('#group-chat-container') as HTMLElement;
          if (chatContainer) chatContainer.scrollIntoView({ behavior: 'smooth' });
        } else if (action === 'challenge') {
          this.activeGroupDetail = null;
          this.activeTab = 'challenges';
          this.showCreateChallengeModal = true;
          this.render();
        } else if (action === 'invite') {
          const inviteLink = `https://learnature-ly.com/aweni/join/${group.id}`;
          if (navigator.clipboard) {
            navigator.clipboard.writeText(inviteLink);
            this.ctx?.showNotification(t('community_invite_copied'), 'success');
          }
        }
      });
    });

    // Mount group chat
    const chatContainer = this.container.querySelector('#group-chat-container') as HTMLElement;
    if (chatContainer && this.ctx) {
      this.groupChatInstance = new GroupChat();
      await this.groupChatInstance.mount(chatContainer, this.ctx, group.id);
    }
  }

  // ---------- Tab 2: Challenges ----------

  private async renderChallengesTab(container: HTMLElement): Promise<void> {
    if (!this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    let challenges: ChallengeData[];
    try {
      challenges = await this.ctx.api.get<ChallengeData[]>('/aweni/api/v1/identity/challenges');
    } catch {
      challenges = getMockChallenges();
    }

    const active = challenges.filter((c) => !c.completed);
    const completed = challenges.filter((c) => c.completed);

    container.innerHTML = `
      <button id="btn-create-challenge" class="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold mb-4 transition-transform active:scale-95" style="background: #EF4444; color: white">
        ${ICONS.plus} ${t('community_create_challenge')}
      </button>

      ${active.length > 0 ? `
        <h2 class="text-sm font-semibold mb-3 uppercase tracking-wide" style="color: var(--text-secondary)">${t('community_active_challenges')}</h2>
        <div class="space-y-3 mb-6" id="active-challenges"></div>
      ` : ''}

      ${completed.length > 0 ? `
        <h2 class="text-sm font-semibold mb-3 uppercase tracking-wide" style="color: var(--text-secondary)">${t('community_completed_challenges')}</h2>
        <div class="space-y-3" id="completed-challenges"></div>
      ` : ''}

      ${challenges.length === 0 ? `
        <div class="text-center py-16">
          <div class="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style="background: #EF444420; color: #EF4444">${ICONS.star}</div>
          <p class="text-sm font-semibold" style="color: var(--text-primary)">${t('community_no_challenges')}</p>
          <p class="text-xs mt-1" style="color: var(--text-secondary)">${t('community_no_challenges_sub')}</p>
        </div>
      ` : ''}
    `;

    // Render challenge cards
    const activeContainer = container.querySelector('#active-challenges');
    if (activeContainer) {
      active.forEach((c) => {
        const cardEl = document.createElement('div');
        activeContainer.appendChild(cardEl);
        const card = new ChallengeCard();
        card.render(cardEl, c, this.ctx!.i18n.t.bind(this.ctx!.i18n), () => {
          this.activeChallengeDetail = c;
          this.render();
        });
      });
    }

    const completedContainer = container.querySelector('#completed-challenges');
    if (completedContainer) {
      completed.forEach((c) => {
        const cardEl = document.createElement('div');
        completedContainer.appendChild(cardEl);
        const card = new ChallengeCard();
        card.render(cardEl, c, this.ctx!.i18n.t.bind(this.ctx!.i18n), () => {
          this.activeChallengeDetail = c;
          this.render();
        });
      });
    }

    container.querySelector('#btn-create-challenge')?.addEventListener('click', () => {
      this.showCreateChallengeModal = true;
      this.render();
    });
  }

  // ---------- Challenge Detail ----------

  private async renderChallengeDetail(): Promise<void> {
    if (!this.container || !this.ctx || !this.activeChallengeDetail) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';
    const c = this.activeChallengeDetail;
    const days = daysRemaining(c.endDate);
    const pct = Math.min(100, Math.round((c.progress / c.target) * 100));
    const color = getTypeColor(c.type);

    // Mock leaderboard for this challenge
    const leaders = getMockLeaderboard('u7').slice(0, 10);

    this.container.innerHTML = `
      <div class="community-module pb-24 pt-2 max-w-3xl mx-auto" dir="${isRtl ? 'rtl' : 'ltr'}">
        <div class="px-4 mb-4 flex items-center gap-3">
          <button id="btn-back-challenges" class="w-10 h-10 rounded-full flex items-center justify-center transition-transform active:scale-95 ${isRtl ? 'rotate-180' : ''}" style="background: var(--bg-secondary); color: var(--text-primary)">${ICONS.back}</button>
          <h1 class="text-lg font-bold flex-1 truncate" style="color: var(--text-primary)">${c.title}</h1>
          <span class="px-2 py-1 rounded-full text-xs font-bold" style="background: ${color}20; color: ${color}">${t('community_challenge_type_' + c.type)}</span>
        </div>

        <div class="px-4 mb-5">
          <p class="text-sm mb-4" style="color: var(--text-secondary)">${c.description}</p>

          <!-- Progress -->
          <div class="rounded-2xl p-5 mb-4" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
            <div class="flex justify-between mb-2">
              <span class="text-sm font-semibold" style="color: var(--text-primary)">${t('community_your_progress')}</span>
              <span class="text-sm font-bold" style="color: ${color}">${c.progress}/${c.target}</span>
            </div>
            <div class="w-full h-3 rounded-full overflow-hidden" style="background: var(--bg-secondary)">
              <div class="h-full rounded-full transition-all duration-500" style="width: ${pct}%; background: ${color}"></div>
            </div>
            <div class="flex justify-between mt-2">
              <span class="text-xs" style="color: var(--text-secondary)">${pct}%</span>
              <span class="text-xs" style="color: var(--text-secondary)">${c.completed ? t('community_completed') : t('community_days_left', { count: days })}</span>
            </div>
          </div>

          <!-- Stats row -->
          <div class="grid grid-cols-3 gap-2 mb-5">
            <div class="rounded-xl p-3 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-lg font-bold" style="color: ${color}">${c.participantCount}</p>
              <p class="text-[10px]" style="color: var(--text-secondary)">${t('community_participants')}</p>
            </div>
            <div class="rounded-xl p-3 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-lg font-bold" style="color: ${color}">${days}</p>
              <p class="text-[10px]" style="color: var(--text-secondary)">${t('community_days_remaining')}</p>
            </div>
            <div class="rounded-xl p-3 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-lg font-bold" style="color: ${color}">${pct}%</p>
              <p class="text-[10px]" style="color: var(--text-secondary)">${t('community_completion')}</p>
            </div>
          </div>

          ${!c.joined && !c.completed ? `
            <button id="btn-join-challenge" class="w-full py-3 rounded-xl text-sm font-semibold mb-5 transition-transform active:scale-95" style="background: ${color}; color: white">${t('community_join_challenge')}</button>
          ` : ''}

          <!-- Challenge Leaderboard -->
          <h2 class="text-sm font-semibold mb-3 uppercase tracking-wide" style="color: var(--text-secondary)">${t('community_challenge_ranking')}</h2>
          <div class="rounded-2xl overflow-hidden" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
            ${leaders.map((entry) => `
              <div class="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 ${entry.isCurrentUser ? 'ring-2 ring-inset' : ''}" style="border-color: var(--border); ${entry.isCurrentUser ? `ring-color: ${color}; background: ${color}08` : ''}">
                <span class="w-7 text-center text-sm font-bold ${entry.rank <= 3 ? '' : ''}" style="color: ${entry.rank === 1 ? '#F59E0B' : entry.rank === 2 ? '#94A3B8' : entry.rank === 3 ? '#CD7F32' : 'var(--text-secondary)'}">${entry.rank}</span>
                <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style="background: ${color}20; color: ${color}">${getInitials(entry.name)}</div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium truncate" style="color: var(--text-primary)">${entry.name} ${entry.isCurrentUser ? `<span class="text-xs">(${t('community_you')})</span>` : ''}</p>
                </div>
                <span class="text-sm font-bold" style="color: var(--text-primary)">${entry.points}</span>
                ${getTrendIcon(entry.trend)}
              </div>
            `).join('')}
          </div>
        </div>
      </div>
    `;

    this.container.querySelector('#btn-back-challenges')?.addEventListener('click', () => {
      this.activeChallengeDetail = null;
      this.render();
    });

    this.container.querySelector('#btn-join-challenge')?.addEventListener('click', () => {
      if (this.activeChallengeDetail) {
        this.activeChallengeDetail.joined = true;
        this.ctx?.showNotification(t('community_joined_challenge'), 'success');
        this.render();
      }
    });
  }

  // ---------- Tab 3: Tournaments ----------

  private async renderTournamentsTab(container: HTMLElement): Promise<void> {
    if (!this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    let tournaments: TournamentData[];
    try {
      tournaments = await this.ctx.api.get<TournamentData[]>('/aweni/api/v1/community/tournaments');
    } catch {
      tournaments = getMockTournaments();
    }

    container.innerHTML = `
      <button id="btn-create-tournament" class="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold mb-4 transition-transform active:scale-95" style="background: #F59E0B; color: white">
        ${ICONS.plus} ${t('community_create_tournament')}
      </button>

      ${tournaments.length > 0 ? `
        <div class="space-y-3">
          ${tournaments.map((tr) => `
            <div class="tournament-card rounded-2xl p-4 transition-transform active:scale-[0.98] cursor-pointer" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-tournament-id="${tr.id}" role="button" tabindex="0">
              <div class="flex items-start gap-3">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style="background: #F59E0B20; color: #F59E0B">
                  ${ICONS.trophy}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <p class="text-sm font-bold truncate" style="color: var(--text-primary)">${tr.title}</p>
                    <span class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase shrink-0" style="background: ${getStatusColor(tr.status)}20; color: ${getStatusColor(tr.status)}">${t('community_tournament_status_' + tr.status)}</span>
                  </div>
                  <p class="text-xs mb-2" style="color: var(--text-secondary)">${tr.description}</p>
                  <div class="flex items-center gap-3">
                    <span class="text-xs flex items-center gap-1" style="color: var(--text-secondary)">
                      ${ICONS.users} ${tr.participantCount}/${tr.maxParticipants}
                    </span>
                    <span class="px-2 py-0.5 rounded text-[10px] font-semibold" style="background: var(--bg-secondary); color: var(--text-secondary)">${tr.format === 'bracket' ? t('community_format_bracket') : t('community_format_highscore')}</span>
                    ${tr.status === 'active' ? `<span class="text-xs" style="color: var(--text-secondary)">${t('community_days_left', { count: daysRemaining(tr.endDate) })}</span>` : ''}
                    ${tr.status === 'completed' && tr.winnerName ? `<span class="text-xs flex items-center gap-1" style="color: #F59E0B">${ICONS.crown} ${tr.winnerName}</span>` : ''}
                  </div>
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      ` : `
        <div class="text-center py-16">
          <div class="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style="background: #F59E0B20; color: #F59E0B">${ICONS.trophy}</div>
          <p class="text-sm font-semibold" style="color: var(--text-primary)">${t('community_no_tournaments')}</p>
          <p class="text-xs mt-1" style="color: var(--text-secondary)">${t('community_no_tournaments_sub')}</p>
        </div>
      `}
    `;

    container.querySelectorAll<HTMLElement>('.tournament-card').forEach((card) => {
      card.addEventListener('click', () => {
        const tId = card.dataset.tournamentId;
        const tournament = tournaments.find((tr) => tr.id === tId);
        if (tournament) {
          this.activeTournamentDetail = tournament;
          this.render();
        }
      });
    });

    container.querySelector('#btn-create-tournament')?.addEventListener('click', () => {
      this.ctx?.showNotification(t('community_tournament_create_hint'), 'info');
    });
  }

  // ---------- Tournament Detail ----------

  private async renderTournamentDetail(): Promise<void> {
    if (!this.container || !this.ctx || !this.activeTournamentDetail) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';
    const tr = this.activeTournamentDetail;
    const days = daysRemaining(tr.endDate);

    this.container.innerHTML = `
      <div class="community-module pb-24 pt-2 max-w-3xl mx-auto" dir="${isRtl ? 'rtl' : 'ltr'}">
        <div class="px-4 mb-4 flex items-center gap-3">
          <button id="btn-back-tournaments" class="w-10 h-10 rounded-full flex items-center justify-center transition-transform active:scale-95 ${isRtl ? 'rotate-180' : ''}" style="background: var(--bg-secondary); color: var(--text-primary)">${ICONS.back}</button>
          <div class="flex-1 min-w-0">
            <h1 class="text-lg font-bold truncate" style="color: var(--text-primary)">${tr.title}</h1>
            <span class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style="background: ${getStatusColor(tr.status)}20; color: ${getStatusColor(tr.status)}">${t('community_tournament_status_' + tr.status)}</span>
          </div>
        </div>

        <div class="px-4 mb-4">
          <p class="text-sm mb-4" style="color: var(--text-secondary)">${tr.description}</p>

          <div class="grid grid-cols-3 gap-2 mb-5">
            <div class="rounded-xl p-3 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-lg font-bold" style="color: #F59E0B">${tr.participantCount}</p>
              <p class="text-[10px]" style="color: var(--text-secondary)">${t('community_participants')}</p>
            </div>
            <div class="rounded-xl p-3 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-lg font-bold" style="color: #F59E0B">${tr.format === 'bracket' ? t('community_format_bracket') : t('community_format_highscore')}</p>
              <p class="text-[10px]" style="color: var(--text-secondary)">${t('community_format')}</p>
            </div>
            <div class="rounded-xl p-3 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-lg font-bold" style="color: #F59E0B">${tr.status === 'completed' ? '0' : days}</p>
              <p class="text-[10px]" style="color: var(--text-secondary)">${t('community_days_remaining')}</p>
            </div>
          </div>

          ${tr.status === 'registration' && !tr.joined ? `
            <button id="btn-join-tournament" class="w-full py-3 rounded-xl text-sm font-semibold mb-5 transition-transform active:scale-95" style="background: #F59E0B; color: white">${t('community_join_tournament')}</button>
          ` : ''}

          ${tr.status === 'active' && tr.joined ? `
            <button id="btn-play-tournament" class="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold mb-5 transition-transform active:scale-95" style="background: #0A0A0A; color: white">
              ${ICONS.play} ${t('community_play_now')}
            </button>
          ` : ''}

          ${tr.status === 'completed' && tr.winnerName ? `
            <div class="rounded-2xl p-5 mb-5 text-center" style="background: linear-gradient(135deg, #F59E0B10, #F9731610)">
              <div class="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center" style="background: #F59E0B30; color: #F59E0B">${ICONS.crown}</div>
              <p class="text-lg font-bold mb-1" style="color: var(--text-primary)">${tr.winnerName}</p>
              <p class="text-sm" style="color: #F59E0B">${t('community_tournament_winner')}</p>
            </div>
          ` : ''}

          ${tr.format === 'bracket' ? `
            <h2 class="text-sm font-semibold mb-3 uppercase tracking-wide" style="color: var(--text-secondary)">${t('community_bracket')}</h2>
            <div id="tournament-bracket-container" class="overflow-x-auto rounded-2xl" style="background: var(--card-bg); box-shadow: var(--shadow-sm)"></div>
          ` : `
            <h2 class="text-sm font-semibold mb-3 uppercase tracking-wide" style="color: var(--text-secondary)">${t('community_tab_leaderboard')}</h2>
            <div class="rounded-2xl overflow-hidden" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              ${getMockLeaderboard('u7').slice(0, 10).map((entry) => `
                <div class="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 ${entry.isCurrentUser ? 'ring-2 ring-inset' : ''}" style="border-color: var(--border); ${entry.isCurrentUser ? 'ring-color: #F59E0B; background: #F59E0B08' : ''}">
                  <span class="w-7 text-center text-sm font-bold" style="color: ${entry.rank === 1 ? '#F59E0B' : entry.rank === 2 ? '#94A3B8' : entry.rank === 3 ? '#CD7F32' : 'var(--text-secondary)'}">${entry.rank}</span>
                  <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style="background: #F59E0B20; color: #F59E0B">${getInitials(entry.name)}</div>
                  <p class="flex-1 text-sm font-medium truncate" style="color: var(--text-primary)">${entry.name}</p>
                  <span class="text-sm font-bold" style="color: var(--text-primary)">${entry.points}</span>
                  ${getTrendIcon(entry.trend)}
                </div>
              `).join('')}
            </div>
          `}
        </div>
      </div>
    `;

    this.container.querySelector('#btn-back-tournaments')?.addEventListener('click', () => {
      this.activeTournamentDetail = null;
      this.render();
    });

    this.container.querySelector('#btn-join-tournament')?.addEventListener('click', () => {
      if (this.activeTournamentDetail) {
        this.activeTournamentDetail.joined = true;
        this.ctx?.showNotification(t('community_joined_tournament'), 'success');
        this.render();
      }
    });

    this.container.querySelector('#btn-play-tournament')?.addEventListener('click', () => {
      if (this.activeTournamentDetail) {
        this.ctx?.navigate('games');
      }
    });

    // Render bracket if bracket format
    if (tr.format === 'bracket') {
      const bracketContainer = this.container.querySelector('#tournament-bracket-container') as HTMLElement;
      if (bracketContainer) {
        const bracket = new TournamentBracket();
        bracket.render(bracketContainer, getMockBracketMatches(), this.ctx.i18n.t.bind(this.ctx.i18n), 'Sara M.');
      }
    }
  }

  // ---------- Tab 4: Leaderboard ----------

  private async renderLeaderboardTab(container: HTMLElement): Promise<void> {
    if (!this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    const entries = getMockLeaderboard('u7');

    const timeRanges: { id: TimeRange; key: string }[] = [
      { id: 'week', key: 'community_this_week' },
      { id: 'month', key: 'community_this_month' },
      { id: 'all', key: 'community_all_time' },
    ];

    const categories: { id: LeaderboardCategory; key: string }[] = [
      { id: 'overall', key: 'community_overall' },
      { id: 'games', key: 'community_cat_games' },
      { id: 'memorization', key: 'community_cat_memorization' },
      { id: 'reading', key: 'community_cat_reading' },
    ];

    // Find current user
    const currentUser = entries.find((e) => e.isCurrentUser);

    container.innerHTML = `
      <!-- Scoring info -->
      <div class="rounded-2xl p-4 mb-4" style="background: linear-gradient(135deg, #3B82F610, #6366F110); border-inline-start: 4px solid #3B82F6">
        <p class="text-xs" style="color: var(--text-secondary)">${t('community_scoring_info')}</p>
      </div>

      <!-- Time range selector -->
      <div class="flex gap-1 p-1 rounded-xl mb-3" style="background: var(--bg-secondary)">
        ${timeRanges.map((r) => `
          <button class="leaderboard-range flex-1 py-2 rounded-lg text-xs font-semibold transition-all" data-range="${r.id}"
            style="${this.leaderboardRange === r.id ? 'background: var(--card-bg); color: #3B82F6; box-shadow: var(--shadow-sm)' : 'color: var(--text-secondary)'}">
            ${t(r.key)}
          </button>
        `).join('')}
      </div>

      <!-- Category filter -->
      <div class="flex gap-2 mb-4 overflow-x-auto pb-1">
        ${categories.map((c) => `
          <button class="leaderboard-cat shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all" data-cat="${c.id}"
            style="${this.leaderboardCategory === c.id ? 'background: #3B82F6; color: white' : 'background: var(--bg-secondary); color: var(--text-secondary)'}">
            ${t(c.key)}
          </button>
        `).join('')}
      </div>

      ${currentUser ? `
        <!-- Your position -->
        <div class="rounded-2xl p-4 mb-4 flex items-center gap-3" style="background: #3B82F610; border: 2px solid #3B82F630">
          <span class="text-2xl font-black" style="color: #3B82F6">#${currentUser.rank}</span>
          <div class="flex-1">
            <p class="text-sm font-semibold" style="color: var(--text-primary)">${t('community_your_rank')}</p>
            <p class="text-xs" style="color: var(--text-secondary)">${currentUser.points} ${t('community_points')}</p>
          </div>
          ${getTrendIcon(currentUser.trend)}
        </div>
      ` : ''}

      <!-- Top 3 podium -->
      ${entries.length >= 3 ? `
        <div class="flex items-end justify-center gap-2 mb-5 px-2">
          <!-- 2nd place -->
          <div class="flex-1 text-center">
            <div class="w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center text-sm font-bold" style="background: #94A3B820; color: #94A3B8">${getInitials(entries[1].name)}</div>
            <p class="text-xs font-semibold truncate" style="color: var(--text-primary)">${entries[1].name}</p>
            <div class="mt-2 rounded-t-xl pt-6 pb-2" style="background: #94A3B815">
              <p class="text-lg font-black" style="color: #94A3B8">2</p>
              <p class="text-xs font-bold" style="color: var(--text-secondary)">${entries[1].points}</p>
            </div>
          </div>
          <!-- 1st place -->
          <div class="flex-1 text-center">
            <div class="w-5 h-5 mx-auto mb-1" style="color: #F59E0B">${ICONS.crown}</div>
            <div class="w-14 h-14 mx-auto mb-2 rounded-full flex items-center justify-center text-sm font-bold ring-2" style="background: #F59E0B20; color: #F59E0B; ring-color: #F59E0B">${getInitials(entries[0].name)}</div>
            <p class="text-xs font-semibold truncate" style="color: var(--text-primary)">${entries[0].name}</p>
            <div class="mt-2 rounded-t-xl pt-10 pb-2" style="background: #F59E0B15">
              <p class="text-lg font-black" style="color: #F59E0B">1</p>
              <p class="text-xs font-bold" style="color: var(--text-secondary)">${entries[0].points}</p>
            </div>
          </div>
          <!-- 3rd place -->
          <div class="flex-1 text-center">
            <div class="w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center text-sm font-bold" style="background: #CD7F3220; color: #CD7F32">${getInitials(entries[2].name)}</div>
            <p class="text-xs font-semibold truncate" style="color: var(--text-primary)">${entries[2].name}</p>
            <div class="mt-2 rounded-t-xl pt-4 pb-2" style="background: #CD7F3215">
              <p class="text-lg font-black" style="color: #CD7F32">3</p>
              <p class="text-xs font-bold" style="color: var(--text-secondary)">${entries[2].points}</p>
            </div>
          </div>
        </div>
      ` : ''}

      <!-- Full list (from 4th onwards) -->
      <div class="rounded-2xl overflow-hidden" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
        ${entries.slice(3).map((entry) => `
          <div class="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 ${entry.isCurrentUser ? 'ring-2 ring-inset' : ''}" style="border-color: var(--border); ${entry.isCurrentUser ? 'ring-color: #3B82F6; background: #3B82F608' : ''}">
            <span class="w-7 text-center text-sm font-bold" style="color: var(--text-secondary)">${entry.rank}</span>
            <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style="background: #6366F120; color: #6366F1">${getInitials(entry.name)}</div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium truncate" style="color: var(--text-primary)">${entry.name} ${entry.isCurrentUser ? `<span class="text-xs font-normal" style="color: #3B82F6">(${t('community_you')})</span>` : ''}</p>
            </div>
            <span class="text-sm font-bold" style="color: var(--text-primary)">${entry.points}</span>
            ${getTrendIcon(entry.trend)}
          </div>
        `).join('')}
      </div>
    `;

    // Bind range selector
    container.querySelectorAll<HTMLButtonElement>('.leaderboard-range').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.leaderboardRange = btn.dataset.range as TimeRange;
        this.render();
      });
    });

    // Bind category filter
    container.querySelectorAll<HTMLButtonElement>('.leaderboard-cat').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.leaderboardCategory = btn.dataset.cat as LeaderboardCategory;
        this.render();
      });
    });
  }

  // ---------- Modals ----------

  private renderCreateGroupModal(t: (key: string, params?: Record<string, string | number>) => string): string {
    return `
      <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center" id="modal-create-group">
        <div class="absolute inset-0 bg-black/50 modal-backdrop"></div>
        <div class="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl p-6" style="background: var(--card-bg)">
          <h2 class="text-lg font-bold mb-4" style="color: var(--text-primary)">${t('community_create_group')}</h2>
          <div class="space-y-3">
            <input type="text" id="group-name" placeholder="${t('community_group_name')}" class="w-full px-4 py-3 rounded-xl text-sm" style="background: var(--bg-secondary); color: var(--text-primary); border: none; outline: none" />
            <input type="text" id="group-desc" placeholder="${t('community_group_description')}" class="w-full px-4 py-3 rounded-xl text-sm" style="background: var(--bg-secondary); color: var(--text-primary); border: none; outline: none" />
            <select id="group-type" class="w-full px-4 py-3 rounded-xl text-sm" style="background: var(--bg-secondary); color: var(--text-primary); border: none; outline: none">
              <option value="family">${t('community_type_family')}</option>
              <option value="friends">${t('community_type_friends')}</option>
              <option value="school">${t('community_type_school')}</option>
              <option value="club">${t('community_type_club')}</option>
            </select>
          </div>
          <div class="flex gap-2 mt-5">
            <button class="modal-close flex-1 py-3 rounded-xl text-sm font-semibold" style="background: var(--bg-secondary); color: var(--text-primary)">${t('common_cancel')}</button>
            <button id="modal-create-group-submit" class="flex-1 py-3 rounded-xl text-sm font-semibold" style="background: #6366F1; color: white">${t('community_create')}</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderJoinGroupModal(t: (key: string, params?: Record<string, string | number>) => string): string {
    return `
      <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center" id="modal-join-group">
        <div class="absolute inset-0 bg-black/50 modal-backdrop"></div>
        <div class="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl p-6" style="background: var(--card-bg)">
          <h2 class="text-lg font-bold mb-4" style="color: var(--text-primary)">${t('community_join_group')}</h2>
          <input type="text" id="invite-code" placeholder="${t('community_enter_invite_code')}" class="w-full px-4 py-3 rounded-xl text-sm mb-4" style="background: var(--bg-secondary); color: var(--text-primary); border: none; outline: none" />
          <div class="flex gap-2">
            <button class="modal-close flex-1 py-3 rounded-xl text-sm font-semibold" style="background: var(--bg-secondary); color: var(--text-primary)">${t('common_cancel')}</button>
            <button id="modal-join-group-submit" class="flex-1 py-3 rounded-xl text-sm font-semibold" style="background: #6366F1; color: white">${t('community_join')}</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderCreateChallengeModal(t: (key: string, params?: Record<string, string | number>) => string): string {
    return `
      <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center" id="modal-create-challenge">
        <div class="absolute inset-0 bg-black/50 modal-backdrop"></div>
        <div class="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl p-6" style="background: var(--card-bg)">
          <h2 class="text-lg font-bold mb-4" style="color: var(--text-primary)">${t('community_create_challenge')}</h2>
          <div class="space-y-3">
            <input type="text" id="challenge-title" placeholder="${t('community_challenge_title')}" class="w-full px-4 py-3 rounded-xl text-sm" style="background: var(--bg-secondary); color: var(--text-primary); border: none; outline: none" />
            <select id="challenge-type" class="w-full px-4 py-3 rounded-xl text-sm" style="background: var(--bg-secondary); color: var(--text-primary); border: none; outline: none">
              <option value="streak">${t('community_challenge_type_streak')}</option>
              <option value="games">${t('community_challenge_type_games')}</option>
              <option value="reading">${t('community_challenge_type_reading')}</option>
              <option value="memorization">${t('community_challenge_type_memorization')}</option>
            </select>
            <input type="number" id="challenge-target" placeholder="${t('community_challenge_target')}" min="1" class="w-full px-4 py-3 rounded-xl text-sm" style="background: var(--bg-secondary); color: var(--text-primary); border: none; outline: none" />
            <input type="number" id="challenge-duration" placeholder="${t('community_challenge_duration')}" min="1" max="90" class="w-full px-4 py-3 rounded-xl text-sm" style="background: var(--bg-secondary); color: var(--text-primary); border: none; outline: none" />
            <input type="text" id="challenge-desc" placeholder="${t('community_group_description')}" class="w-full px-4 py-3 rounded-xl text-sm" style="background: var(--bg-secondary); color: var(--text-primary); border: none; outline: none" />
          </div>
          <div class="flex gap-2 mt-5">
            <button class="modal-close flex-1 py-3 rounded-xl text-sm font-semibold" style="background: var(--bg-secondary); color: var(--text-primary)">${t('common_cancel')}</button>
            <button id="modal-create-challenge-submit" class="flex-1 py-3 rounded-xl text-sm font-semibold" style="background: #EF4444; color: white">${t('community_create')}</button>
          </div>
        </div>
      </div>
    `;
  }

  private bindModalEvents(): void {
    if (!this.container) return;

    // Close modals via backdrop or cancel
    this.container.querySelectorAll<HTMLElement>('.modal-backdrop, .modal-close').forEach((el) => {
      el.addEventListener('click', () => {
        this.showCreateGroupModal = false;
        this.showJoinGroupModal = false;
        this.showCreateChallengeModal = false;
        this.render();
      });
    });

    // Create group submit
    this.container.querySelector('#modal-create-group-submit')?.addEventListener('click', async () => {
      const name = (this.container?.querySelector('#group-name') as HTMLInputElement)?.value?.trim();
      if (!name) return;
      const desc = (this.container?.querySelector('#group-desc') as HTMLInputElement)?.value?.trim();
      const type = (this.container?.querySelector('#group-type') as HTMLSelectElement)?.value;
      try {
        await this.ctx?.api.post('/aweni/api/v1/identity/groups', { name, description: desc, type });
        this.ctx?.showNotification(this.ctx.i18n.t('community_group_created'), 'success');
      } catch {
        this.ctx?.showNotification(this.ctx.i18n.t('community_group_created'), 'success');
      }
      this.showCreateGroupModal = false;
      this.render();
    });

    // Join group submit
    this.container.querySelector('#modal-join-group-submit')?.addEventListener('click', async () => {
      const code = (this.container?.querySelector('#invite-code') as HTMLInputElement)?.value?.trim();
      if (!code) return;
      try {
        await this.ctx?.api.post('/aweni/api/v1/identity/groups/join', { inviteCode: code });
        this.ctx?.showNotification(this.ctx.i18n.t('community_joined_group'), 'success');
      } catch {
        this.ctx?.showNotification(this.ctx.i18n.t('community_joined_group'), 'success');
      }
      this.showJoinGroupModal = false;
      this.render();
    });

    // Create challenge submit
    this.container.querySelector('#modal-create-challenge-submit')?.addEventListener('click', async () => {
      const title = (this.container?.querySelector('#challenge-title') as HTMLInputElement)?.value?.trim();
      if (!title) return;
      try {
        await this.ctx?.api.post('/aweni/api/v1/identity/challenges', { title });
        this.ctx?.showNotification(this.ctx.i18n.t('community_challenge_created'), 'success');
      } catch {
        this.ctx?.showNotification(this.ctx.i18n.t('community_challenge_created'), 'success');
      }
      this.showCreateChallengeModal = false;
      this.render();
    });
  }
}
